/**
 * Who owns a gallery object, as a key segment that does not name them.
 *
 * The renderer twin of `src/main/utils/galleryOwnerSegment.ts` — read that one
 * for why the address had to come out of a string every event guest receives,
 * why this is cleanup rather than the fix (the studio de-identifies gallery
 * keys for the whole fleet when it signs them), and why only an address is
 * ever replaced.
 *
 * Web Crypto rather than node's, because this runs in the renderer, which is
 * why it is async where the main-process one is not. `crypto.subtle` is
 * already used here for file hashing in `pages/view/Capture.tsx`.
 *
 * Must produce the same value as the studio's `lib/gallery/galleryOwnerSegment.ts`
 * and the main process's copy: `sha256(address.trim().toLowerCase())`, first 32
 * hex characters. `Operator@Example.com` hashes to
 * `5da87e12d60dd043031d0cddabe2311a`.
 */
export async function galleryOwnerSegment(owner: string | null | undefined): Promise<string> {
  const normalized = typeof owner === 'string' ? owner.trim().toLowerCase() : ''
  if (!normalized) return ''
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return ''
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(normalized))
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32)
}

/**
 * The owner segment to put in a key: an address becomes its hash, and anything
 * else is rendered exactly as `${owner}` always rendered it. Falls back to the
 * address itself when it cannot be hashed — an unavailable `crypto.subtle` in
 * some future embedding, say — because an upload must never fail over the
 * spelling of a folder, and the studio de-identifies that spelling anyway.
 */
export async function galleryOwnerPath(owner: string | null | undefined): Promise<string> {
  if (typeof owner !== 'string' || !owner.includes('@')) return `${owner}`
  try {
    return (await galleryOwnerSegment(owner)) || owner
  } catch {
    return owner
  }
}
